import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/core/combobox';

import type { AriaProps, FieldInputValue, Option } from './types';
import { asString, optionLabel } from './value-helpers';

type SingleSelectComboboxProps = AriaProps & {
  options: Option[];
  value: FieldInputValue;
  onChange: (value: FieldInputValue) => void;
  onSearch?: (search: string) => void;
  emptyText: string;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  className?: string;
  /** Whether to render the inline clear (X) button. Off for required pickers. */
  showClear?: boolean;
};

export function SingleSelectCombobox({
  options,
  value,
  onChange,
  onSearch,
  emptyText,
  onBlur,
  disabled,
  placeholder,
  id,
  className,
  showClear = true,
  ...aria
}: SingleSelectComboboxProps) {
  return (
    <Combobox
      items={options}
      // Server-search results are already filtered; preloaded lists omit this
      // prop so base-ui filters locally.
      filteredItems={onSearch ? options : undefined}
      value={asString(value) || null}
      disabled={disabled}
      itemToStringLabel={(item) => optionLabel(options, item)}
      onValueChange={(next) => onChange(typeof next === 'string' ? next : undefined)}
      onInputValueChange={
        onSearch
          ? (inputValue, eventDetails) => {
              if (eventDetails.reason === 'item-press') {
                return;
              }
              if (typeof inputValue === 'string') {
                onSearch(inputValue);
              }
            }
          : undefined
      }
    >
      <ComboboxInput
        {...aria}
        id={id}
        className={className}
        placeholder={placeholder}
        disabled={disabled}
        showClear={showClear}
        onBlur={onBlur}
      />
      <ComboboxContent>
        <ComboboxEmpty>{emptyText}</ComboboxEmpty>
        <ComboboxList>
          {(option: Option) => (
            <ComboboxItem key={option.value} value={option.value}>
              {option.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
